import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { Chrome } from './chrome';
import { MoreDrawer } from './more-drawer';
import { LoginPage } from './components/login-page';
import { OfflineBanner } from './components/offline-banner';
import { ConfirmDialogHost } from './components/confirm-dialog';
import { TimeLogsPage } from './pages/time-logs';
import { StudioPage } from './pages/studio';
import { OnboardPage } from './pages/onboard';
import { SettingsPage } from './pages/settings';
import { ApcmFallbackPage } from './pages/apcm-fallback';
import { FaxInboxPage } from './pages/fax-inbox';
import { SmsBotPage } from './pages/sms-bot';
import { OrderChatPage } from './pages/order-chat';
import { VoiceLogPage } from './pages/voice-log';
import { NoteExpanderEntry } from './pages/note-expander-entry';
import { LettersPage } from './pages/letters';
import { LabOrdersPage } from './pages/lab-orders';
import { IntakeReviewPage } from './pages/intake-review.jsx';
import { SchedulingPage } from './pages/scheduling.jsx';
import { KioskAdminPage } from './pages/kiosk-admin.jsx';
import { UserMgmtPage } from './pages/usermgmt.jsx';
import { AssessmentKioskAdminPage } from './pages/assessment-kiosk-admin.jsx';
import { getPref, hasPref, setPref } from './lib/user-prefs';
import { firstVisibleTab, getVisibleTabs, isKnownTab, normalizeHiddenTabs, normalizeToolbarOrder, shouldAutoRouteToZephyr, tabsForRole, TABS } from './lib/tabs';
import { ccmApi } from './lib/api';
import { hydrateNavBadges } from './lib/nav-badge-counts';
import './styles/base.css';
import './styles/warm-teal.css';
import './styles/ipad.css';
import './styles/mobile.css';

// CPM visibility: providers + admins only (see the pain_enabled gate below).
function canSeePain(user) {
  const role = String(user?.role || '').toLowerCase();
  return role === 'admin' || role === 'provider';
}

function monthLabel(d) {
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

function dateMonthKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function App() {
  // Gate the first paint on /auth/me. authState transitions:
  //   'checking' → 'login' (on 401) → 'ok' (after successful Google sign-in)
  //   'checking' → 'ok' (already authenticated, cookie still valid)
  // The 'login' phase renders <LoginPage/> in place — same / URL, no v2
  // redirect.
  const [authState, setAuthState] = useState('checking');
  // A ?tab= deep link (e.g. the PWA "Order Chat" shortcut → /?tab=orderchat).
  // Computed once. When it names a known tab it wins over the saved default AND
  // is exempt from the "reset to first visible tab" guard below, so the link
  // lands on its target even if the user hid that tab from their toolbar.
  const deepLinkTab = useMemo(() => {
    try {
      const requested = new URLSearchParams(window.location.search).get('tab');
      return requested && isKnownTab(requested) ? requested : null;
    } catch { return null; }
  }, []);
  // ?rec=1 alongside ?tab=voicelog (the PWA "Log time by voice" shortcut) arms
  // the Voice Log record button: the page auto-starts ONLY if the mic
  // permission is already granted, else it just renders ready-to-tap.
  const deepLinkRec = useMemo(() => {
    try { return new URLSearchParams(window.location.search).get('rec') === '1'; } catch { return false; }
  }, []);
  // ?session=<id> alongside ?tab=orderchat (the voice-memo → Order Chat
  // hand-off deep link): resume that exact conversation on arrival.
  const deepLinkSession = useMemo(() => {
    try { return new URLSearchParams(window.location.search).get('session') || null; } catch { return null; }
  }, []);
  const [tab, setTab] = useState(() => deepLinkTab || getPref('defaultTab'));
  // One-shot resume signal for Order Chat ({sessionId, ts}, consumed once —
  // same shape as the voice arm one-shot). Set by the deep link and by the
  // Voice Log's "Open in Order Chat" hand-off.
  const [orderChatResume, setOrderChatResume] = useState(() => (
    deepLinkTab === 'orderchat' && deepLinkSession ? { sessionId: deepLinkSession, ts: Date.now() } : null
  ));
  // One-shot arm signal for the Voice Log page ({ts} object, consumed once —
  // same shape as the focusPatient one-shot below). Set by the ?rec=1 deep
  // link and the Alt+V shortcut.
  const [voiceArmRequest, setVoiceArmRequest] = useState(() => (
    deepLinkTab === 'voicelog' && deepLinkRec ? { ts: Date.now() } : null
  ));
  const [voicelogBadge, setVoicelogBadge] = useState(0);
  const [date, setDate] = useState(() => new Date());
  const [user, setUser] = useState({ name: '' });
  const [moreOpen, setMoreOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [smsBotOpen, setSmsBotOpen] = useState(false);
  const [onboardBadge, setOnboardBadge] = useState(0);
  const [density, setDensityState] = useState(() => getPref('density'));
  const setDensity = (v) => { setDensityState(v); setPref('density', v); };
  const [hideNoChronic, setHideNoChronicState] = useState(() => getPref('hideNoChronic'));
  const setHideNoChronic = (v) => { setHideNoChronicState(v); setPref('hideNoChronic', v); };
  const [hiddenOnboardTags, setHiddenOnboardTagsState] = useState(() => getPref('hiddenOnboardTags'));
  const setHiddenOnboardTags = (tags) => { setHiddenOnboardTagsState(tags); setPref('hiddenOnboardTags', tags); };
  const [toolbarOrder, setToolbarOrderState] = useState(() => getPref('toolbarOrder'));
  const [hiddenTabs, setHiddenTabsState] = useState(() => getPref('hiddenTabs'));
  const [programFilter, setProgramFilterState] = useState(() => getPref('programFilter') || 'ccm');
  // Tracks whether the admin has explicitly chosen a programFilter (vs the
  // 'ccm' default fallback). Used to decide whether to auto-promote to 'all'
  // when bhi_enabled first becomes true — never overwrite an explicit choice.
  const [programFilterExplicit, setProgramFilterExplicit] = useState(() => hasPref('programFilter'));
  const setProgramFilter = (v) => {
    setProgramFilterState(v);
    setProgramFilterExplicit(true);
    setPref('programFilter', v);
  };
  const [bhiEnabled, setBhiEnabled] = useState(false);
  const [painEnabled, setPainEnabled] = useState(false);
  const [pinEnabled, setPinEnabled] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  // Deep-link target for the "Log time →" links in the Push-to-EMR modal —
  // when set, Time Logs scrolls the row into view and briefly highlights it.
  // Cleared after the page consumes it.
  const [focusPatient, setFocusPatient] = useState(null);
  // Complementary focus target for jumping from a Time Logs row's "›" button
  // into Studio with that patient pre-selected.
  const [focusStudioPatient, setFocusStudioPatient] = useState(null);
  const [focusApcmPatient, setFocusApcmPatient] = useState(null);
  const [apcmBadge, setApcmBadge] = useState(0);
  const [atRiskBadge, setAtRiskBadge] = useState(0);
  // Nav-badge hydration error + manual retry trigger. When the critical
  // patient fetch fails we surface this instead of silently showing 0 counts.
  const [badgeError, setBadgeError] = useState(false);
  const [badgeRetry, setBadgeRetry] = useState(0);
  const visibleToolbarTabs = useMemo(
    () => getVisibleTabs(toolbarOrder, hiddenTabs),
    [toolbarOrder, hiddenTabs]
  );
  const visibleTabKey = visibleToolbarTabs.map(t => t.v).join('|');

  const saveToolbarOrder = (order) => {
    const nextOrder = normalizeToolbarOrder(order);
    const nextHidden = normalizeHiddenTabs(hiddenTabs, nextOrder);
    setToolbarOrderState(nextOrder);
    setHiddenTabsState(nextHidden);
    setPref('toolbarOrder', nextOrder);
    setPref('hiddenTabs', nextHidden);
  };

  const saveHiddenTabs = (tabs) => {
    const nextHidden = normalizeHiddenTabs(tabs, toolbarOrder);
    setHiddenTabsState(nextHidden);
    setPref('hiddenTabs', nextHidden);
  };

  // Switch to Time Logs with a focus hint. Called by Studio when the push
  // modal's "Log time →" deep-link fires.
  const jumpToTimePatient = (patientId) => {
    setFocusPatient({ id: patientId, ts: Date.now() });
    setTab('timelogs');
  };

  // Switch to Studio with a focus hint. Called by Time Logs when the row's
  // "›" Open patient detail button is clicked.
  const jumpToStudioPatient = (patientId) => {
    setFocusStudioPatient({ id: patientId, ts: Date.now() });
    setTab('studio');
  };

  const jumpToApcmPatient = (patientId) => {
    setFocusApcmPatient({ id: patientId, ts: Date.now() });
    setTab('apcm');
  };

  // One place a session is adopted, whichever way the user arrived: the cookie
  // was already valid on load, or they just finished Google sign-in.
  //
  // It always re-pulls /auth/me. The sign-in response (POST /auth/google/staff,
  // server/routes/auth.js `responseBody.user`) carries id/email/displayName/
  // role/mustChangePassword and NOTHING about permissions — no `features`, no
  // `featurePermissionsEnabled`. Trusting it left a freshly signed-in user with
  // an empty feature set until their next reload: every non-admin saw every tab,
  // and an intake-only clerk was never routed to Zephyr. /auth/me is the one
  // authority for permissions, so the sign-in path IS this path. (Zephyr does
  // the same — zephyr-main.jsx passes loadMe as onAuthenticated.)
  //
  // A fetch, not a reload, so an in-progress route hash survives as before.
  const adoptSession = useCallback(() => {
    fetch('/auth/me', { credentials: 'include' })
      .then(async (r) => {
        if (!r.ok) throw new Error('unauthenticated');
        const d = await r.json();
        // /auth/me returns the user fields at the top level:
        //   { id, email, displayName, role, active, features, ... }
        // (No `user` wrapper — confirmed in server/routes/auth.js.)
        // R2/S16: someone whose only grant is Intake works the front desk, not
        // this app — send them to Zephyr BEFORE the SPA renders, so the
        // clinical shell never paints (or starts fetching) on the way past.
        // R7/S34: on every landing, deep link or not — no `?tab=` reaches the
        // main app for this class of user, so the URL is not an input.
        if (shouldAutoRouteToZephyr({
          role: d.role,
          features: d.features,
          featurePermissionsEnabled: d.featurePermissionsEnabled === true,
        })) {
          window.location.replace('/zephyr');
          return;
        }
        setUser({
          id: d.id || '',
          email: d.email || '',
          displayName: d.displayName || '',
          role: d.role || '',
          name: d.displayName || d.email || '',
          features: d.features || [],
          featurePermissionsEnabled: d.featurePermissionsEnabled === true,
        });
        setAuthState('ok');
      })
      .catch(() => {
        // Show the in-app login page rather than bouncing to /v2. Same URL
        // before and after sign-in. A re-pull that fails right after a
        // successful sign-in lands here too — the cookie is set, so the next
        // attempt succeeds without another Google round trip.
        setAuthState('login');
      });
  }, []);

  useEffect(() => { adoptSession(); }, [adoptSession]);

  const handleAuthenticated = () => { adoptSession(); };

  useEffect(() => {
    document.body.setAttribute('data-density', density);
  }, [density]);

  // Load practice-level bhi_enabled flag once auth is OK. When false (default),
  // the chrome CCM/BHI toggle stays hidden and pages render their CCM-only
  // behavior — Phase 3 wiring; consumers attach in Phases 4-6.
  useEffect(() => {
    if (authState !== 'ok') return;
    ccmApi.getSettings()
      .then((s) => {
        setBhiEnabled(s?.bhi_enabled === true);
        // CPM (G3002) is provider-personal — clinical-staff minutes never
        // count — so the pain lens/program surfaces only exist for providers
        // and admins. Staff can't accrue billable CPM time and shouldn't see
        // the program at all. (programFilter==='pain' resets via the effect
        // below when this stays false.)
        setPainEnabled(s?.pain_enabled === true && canSeePain(user));
        // PIN is staff-visible; this is solely the practice enablement flag.
        setPinEnabled(s?.pin_enabled === true);
        setSettingsLoaded(true);
      })
      .catch(() => {});
  }, [authState, user]);

  // One-time auto-promote: when bhi_enabled first becomes true AND the admin
  // hasn't explicitly picked a programFilter yet, switch the view to 'all' so
  // they see both programs side-by-side immediately. Does not run again once
  // they switch segments (setProgramFilter flips programFilterExplicit to
  // true and persists the choice).
  useEffect(() => {
    if (bhiEnabled && !programFilterExplicit && programFilter === 'ccm') {
      setProgramFilterState('all');
      setProgramFilterExplicit(true);
      setPref('programFilter', 'all');
    }
  }, [bhiEnabled, programFilterExplicit, programFilter]);

  // A persisted 'pain' lens with the flag since turned off would strand the
  // user in a view no toggle can leave — snap back to CCM. Wait for the
  // settings fetch to resolve before judging (painEnabled starts false).
  useEffect(() => {
    if (settingsLoaded && !painEnabled && programFilter === 'pain') {
      setProgramFilter('ccm');
    }
  }, [settingsLoaded, painEnabled, programFilter]);

  // A persisted 'pin' lens with the flag since turned off would strand the
  // user in a view no toggle can leave — snap back to CCM. Wait for the
  // settings fetch to resolve before judging (pinEnabled starts false).
  useEffect(() => {
    if (settingsLoaded && !pinEnabled && programFilter === 'pin') {
      setProgramFilter('ccm');
    }
  }, [settingsLoaded, pinEnabled, programFilter]);

  useEffect(() => {
    if (authState !== 'ok') return;
    if (visibleToolbarTabs.some(t => t.v === tab)) return;
    if (tab === deepLinkTab) return; // explicit deep link wins, even if hidden
    setTab(firstVisibleTab(toolbarOrder, hiddenTabs));
  }, [authState, tab, toolbarOrder, hiddenTabs, visibleTabKey, visibleToolbarTabs, deepLinkTab]);

  // Feature-permission redirect: when enforcement is on and the active tab is
  // not in the user's visible set, snap to the first permitted tab so staff
  // can't see a blank page after a role change.
  useEffect(() => {
    if (!user?.featurePermissionsEnabled) return;
    const visible = tabsForRole(TABS, {
      role: user?.role, features: user?.features, featurePermissionsEnabled: true,
    }).map((t) => t.v);
    if (visible.length && !visible.includes(tab)) setTab(visible[0]);
  }, [user, tab]);

  // Hydrate nav badges from the shell. Individual pages still refresh these
  // after tab-local writes, but the chrome should not stay blank until each
  // tab has been opened in the current session.
  useEffect(() => {
    if (authState !== 'ok') return;
    let cancelled = false;
    hydrateNavBadges(ccmApi, { monthKey: dateMonthKey(date), programFilter }).then((res) => {
      if (cancelled) return;
      if (res.ok) {
        // A null badge means that one count couldn't be computed (a predicate
        // threw on a bad row); leave its prior value rather than blanking it.
        if (res.badges.apcm != null) setApcmBadge(res.badges.apcm);
        if (res.badges.onboard != null) setOnboardBadge(res.badges.onboard);
        if (res.badges.atRisk != null) setAtRiskBadge(res.badges.atRisk);
        setBadgeError(false);
      } else {
        // Patient list couldn't load: surface it (retryable) and leave the
        // existing counts in place rather than implying "nothing to do".
        setBadgeError(true);
      }
    }).catch((e) => {
      // Last-resort guard. hydrateNavBadges isolates count failures internally,
      // so reaching here is unexpected — log it rather than swallow (a silent
      // catch here is exactly what hid the conditionLabel crash).
      try { console.error('[nav-badge] hydrate failed', e); } catch (_) { /* noop */ }
      if (!cancelled) setBadgeError(true);
    });
    return () => { cancelled = true; };
  }, [authState, date, programFilter, badgeRetry]);

  useEffect(() => {
    const apply = () => {
      const coarse = window.matchMedia('(pointer: coarse)').matches;
      const narrow = window.innerWidth <= 1024;
      if (!coarse && !narrow) {
        document.body.removeAttribute('data-ipad');
        return;
      }
      const portrait = window.innerHeight > window.innerWidth;
      document.body.setAttribute('data-ipad', portrait ? 'stack' : 'split');
    };
    apply();
    const mq = window.matchMedia('(pointer: coarse)');
    mq.addEventListener('change', apply);
    window.addEventListener('resize', apply);
    return () => {
      mq.removeEventListener('change', apply);
      window.removeEventListener('resize', apply);
    };
  }, []);

  // Global Alt+V → jump to Voice Log with the record button armed. Ignored
  // while typing (input/textarea/select/contenteditable). e.code KeyV is the
  // reliable check — macOS Alt+V yields e.key '√'.
  useEffect(() => {
    if (authState !== 'ok') return undefined;
    const onKey = (e) => {
      if (!e.altKey || e.ctrlKey || e.metaKey) return;
      if (e.code !== 'KeyV' && e.key !== 'v' && e.key !== 'V') return;
      const t = e.target;
      const tag = t && t.tagName ? t.tagName.toLowerCase() : '';
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || (t && t.isContentEditable)) return;
      e.preventDefault();
      setTab('voicelog');
      setVoiceArmRequest({ ts: Date.now() });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [authState]);

  const prev = () => setDate((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  const next = () => setDate((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1));
  const today = () => setDate(new Date());

  if (authState === 'login') return <LoginPage onAuthenticated={handleAuthenticated} />;
  if (authState !== 'ok') return null;

  return (
    <>
      <OfflineBanner />
      <ConfirmDialogHost />
      {badgeError && (
        <div className="badge-load-error" role="alert"
          style={{ background: '#fde8e8', color: '#7a1f1f', padding: '6px 14px', fontSize: 13,
                   display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'center' }}>
          <span>⚠ Couldn’t refresh patient counts — the tab badges may be out of date.</span>
          <button className="btn ghost sm" onClick={() => setBadgeRetry((n) => n + 1)}>Retry</button>
        </div>
      )}
      <Chrome
        tab={tab} setTab={setTab}
        month={monthLabel(date)} onPrevMonth={prev} onNextMonth={next} onToday={today}
        user={user} onOpenMore={() => setMoreOpen(true)}
        tabBadges={{ apcm: apcmBadge, onboard: onboardBadge, studio: atRiskBadge, voicelog: voicelogBadge }}
        toolbarTabs={visibleToolbarTabs}
        programFilter={programFilter}
        setProgramFilter={setProgramFilter}
        bhiEnabled={bhiEnabled}
        pinEnabled={pinEnabled}
        painEnabled={painEnabled}
      />
      <div className="app">
        {tab === 'timelogs' && (
          <TimeLogsPage
            month={dateMonthKey(date)}
            focusPatient={focusPatient}
            onFocusConsumed={() => setFocusPatient(null)}
            onOpenPatientInStudio={jumpToStudioPatient}
            hideNoChronic={hideNoChronic}
            currentUser={user}
            programFilter={programFilter}
            bhiEnabled={bhiEnabled}
            painEnabled={painEnabled}
            pinEnabled={pinEnabled}
          />
        )}
        {tab === 'studio'   && (
          <StudioPage
            month={dateMonthKey(date)}
            onJumpToTimePatient={jumpToTimePatient}
            onJumpToApcmPatient={jumpToApcmPatient}
            focusPatient={focusStudioPatient}
            onFocusConsumed={() => setFocusStudioPatient(null)}
            hideNoChronic={hideNoChronic}
            currentUser={user}
            programFilter={programFilter}
            setProgramFilter={setProgramFilter}
          />
        )}
        {tab === 'apcm' && (
          <ApcmFallbackPage
            month={dateMonthKey(date)}
            focusPatient={focusApcmPatient}
            onFocusConsumed={() => setFocusApcmPatient(null)}
            onBadgeChange={setApcmBadge}
            hideNoChronic={hideNoChronic}
            programFilter={programFilter}
            bhiEnabled={bhiEnabled}
          />
        )}
        {tab === 'onboard' && (
          <OnboardPage
            month={dateMonthKey(date)}
            onBadgeChange={setOnboardBadge}
            hideNoChronic={hideNoChronic}
            hiddenOnboardTags={hiddenOnboardTags}
            programFilter={programFilter}
            bhiEnabled={bhiEnabled}
            painEnabled={painEnabled}
            pinEnabled={pinEnabled}
            currentUser={user}
          />
        )}
        {tab === 'faxinbox' && (
          <FaxInboxPage currentUser={user} />
        )}
        {tab === 'orderchat' && (
          <OrderChatPage
            resumeRequest={orderChatResume}
            onResumeConsumed={() => setOrderChatResume(null)}
          />
        )}
        {tab === 'voicelog' && (
          <VoiceLogPage
            currentUser={user}
            armRequest={voiceArmRequest}
            onArmConsumed={() => setVoiceArmRequest(null)}
            onReviewCountChange={setVoicelogBadge}
            onJumpToTimePatient={jumpToTimePatient}
            onOpenOrderChat={(sessionId) => {
              setOrderChatResume({ sessionId, ts: Date.now() });
              setTab('orderchat');
            }}
          />
        )}
        {tab === 'noteexpander' && (
          <NoteExpanderEntry />
        )}
        {tab === 'letters' && <LettersPage />}
        {tab === 'questhomebound' && <LabOrdersPage />}
        {tab === 'intake' && <IntakeReviewPage />}
        {tab === 'scheduling' && <SchedulingPage user={user} onOpenSettings={() => setSettingsOpen(true)} settingsOpen={settingsOpen} />}
        {tab === 'kiosk' && <KioskAdminPage user={user} />}
        {tab === 'usermgmt' && <UserMgmtPage user={user} />}
        {tab === 'bhi-assessment-admin' && <AssessmentKioskAdminPage user={user} />}
      </div>
      <MoreDrawer
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        density={density}
        setDensity={setDensity}
        onOpenSettings={() => setSettingsOpen(true)}
        onOpenSmsBot={() => setSmsBotOpen(true)}
      />
      {smsBotOpen && <SmsBotPage onClose={() => setSmsBotOpen(false)} user={user} />}
      <SettingsPage
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        density={density}
        setDensity={setDensity}
        hideNoChronic={hideNoChronic}
        setHideNoChronic={setHideNoChronic}
        hiddenOnboardTags={hiddenOnboardTags}
        setHiddenOnboardTags={setHiddenOnboardTags}
        toolbarOrder={toolbarOrder}
        hiddenTabs={hiddenTabs}
        onToolbarOrderChange={saveToolbarOrder}
        onHiddenTabsChange={saveHiddenTabs}
        user={user}
        onBhiEnabledChange={setBhiEnabled}
        onPainEnabledChange={(v) => setPainEnabled(v && canSeePain(user))}
        onPinEnabledChange={setPinEnabled}
      />
    </>
  );
}

const el = document.getElementById('app');
if (el) createRoot(el).render(<App />);
